import React from 'react';
import { ArrowLeft, RefreshCw, WifiOff, FileWarning } from 'lucide-react';
import logo from '../../assets/images/logo.png';
import styles from './ErrorPage.module.css';

interface ErrorPageProps {
  isLoadingError: boolean;
  message?: string;
  isReloading?: boolean;
  onReload: () => void;
  onGoBack?: () => void;
}

// Shared by route failures and startup failures. Keep this independent of the
// router, store, and runtime configuration so it can render before app startup.
const ErrorPage: React.FC<ErrorPageProps> = ({
  isLoadingError,
  message,
  isReloading = false,
  onReload,
  onGoBack,
}) => (
  <div className={styles.page}>
    <section className={styles.card} role="alert" aria-live="polite">
      <img src={logo} alt="VaultiScan" className={styles.brand} />
      <div className={styles.icon} aria-hidden="true">
        {isLoadingError ? <WifiOff size={28} strokeWidth={1.7} /> : <FileWarning size={28} strokeWidth={1.7} />}
      </div>
      <p className={styles.eyebrow}>{isLoadingError ? 'Loading error' : 'Page error'}</p>
      <h2 className={styles.title}>
        {isLoadingError ? 'Let’s get you reconnected' : 'Something didn’t go as planned'}
      </h2>
      <p className={styles.message}>
        {message || (isLoadingError
          ? 'We couldn’t load this page. Check your connection and try again.'
          : 'We couldn’t display this page. Please try again in a moment.')}
      </p>

      {isReloading ? (
        <div className={styles.reloading} role="status">
          <RefreshCw className={styles.spinning} size={17} aria-hidden="true" />
          Attempting to reconnect…
        </div>
      ) : (
        <>
          {isLoadingError && (
            <div className={styles.hint}>
              A temporary connection issue or a recent update may be preventing this page from loading.
            </div>
          )}
          <div className={styles.actions}>
            <button type="button" onClick={onReload} className={styles.primaryButton}>
              <RefreshCw size={16} aria-hidden="true" />
              Try again
            </button>
            {onGoBack && (
              <button type="button" onClick={onGoBack} className={styles.secondaryButton}>
                <ArrowLeft size={16} aria-hidden="true" />
                Go back
              </button>
            )}
          </div>
          <p className={styles.footer}>Still having trouble? Please contact your administrator.</p>
        </>
      )}
    </section>
  </div>
);

export default ErrorPage;
